import { transformSearch } from "@/api/helper.ts";
import { queryClient } from "@/query-client.ts";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import invoiceService from "../services/invoice-service";

export const invoiceSearchSchema = z.object({
	search: z.string().optional(),
	include: z.string().optional(),
	page: z.number().nullable().optional(),
	per_page: z.number().optional(),
});

export const invoicesQueryOptions = (search?: z.infer<typeof invoiceSearchSchema>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["invoices", search],
		queryFn: () => invoiceService.list(transformSearch(search, ["search"])),
		...options,
	});
};

export const invoiceQueryOptions = (invoiceId: string) => {
	return queryOptions({
		queryKey: ["invoice", invoiceId],
		queryFn: () => invoiceService.findById(invoiceId),
	});
};

export const invoiceJournalItemsQueryOptions = (invoiceId: string, options?: { enabled?: boolean }) => {
	return queryOptions({
		queryKey: ["invoice", invoiceId, "journal-items"],
		queryFn: () => invoiceService.getJournalItems(invoiceId),
		staleTime: 60_000,
		...options,
	});
};

export const useInvoiceCreateMutation = (invoiceId?: string) => {
	return useMutation({
		mutationKey: ["invoice", invoiceId],
		mutationFn: invoiceService.create,
		meta: {
			successMessage: "Invoice created successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoice"] }),
	});
};

export const useInvoicePreviewMutation = () => {
	return useMutation({
		mutationKey: ["invoice_preview"],
		mutationFn: invoiceService.preview,
	});
};

export const usePostInvoiceJournalEntryMutation = (invoiceId: string) => {
	return useMutation({
		mutationKey: ["invoice", invoiceId, "journal-entries", "post"],
		mutationFn: (status: "draft" | "posted" = "posted") => invoiceService.postJournalEntry(invoiceId, status),
		meta: {
			successMessage: "Journal entry sent to Books",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoice", invoiceId, "journal-items"] }),
	});
};

export const contractInvoicesQueryOptions = (contractId: string) => {
	return queryOptions({
		queryKey: ["invoices", "contract", contractId],
		queryFn: () => invoiceService.list({ sales_contract_id: contractId }),
		enabled: !!contractId,
	});
};
